import { useAnalyticsContext } from '@prezly/analytics-nextjs';
import type { ExtendedStory } from '@prezly/sdk';
import type { Node } from '@prezly/story-content-format';
import { DocumentNode, ImageNode } from '@prezly/story-content-format';
import { isEmbargoStory } from '@prezly/theme-kit-core';
import { translations } from '@prezly/theme-kit-intl';
import { StorySeo } from '@prezly/theme-kit-nextjs';
import Image from '@prezly/uploadcare-image';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { StoryPublicationDate } from '@/components';
import { getStoryThumbnail } from '@/components/StoryImage/lib';
import { useThemeSettings } from '@/hooks';
import { ButtonLink } from '@/ui';

import Layout from '../Layout';

import { Share } from './Share';

import styles from './Story.module.scss';

const CategoriesList = dynamic(() => import('@/components/CategoriesList'));
const ContentRenderer = dynamic(() => import('@/components/ContentRenderer'));
const Embargo = dynamic(() => import('./Embargo'));

type Props = {
    story: ExtendedStory;
};

function extractContactNodes(original: DocumentNode): DocumentNode {
    return {
        ...original,
        children: original.children.filter((child) => child.type === 'contact'),
    };
}

function removeContactNodes(original: DocumentNode): DocumentNode {
    return {
        ...original,
        children: original.children.filter((child) => child.type !== 'contact'),
    };
}

function getFilteredNodes(nodes: Node | Node[]): Node | Node[] {
    if (DocumentNode.isDocumentNode(nodes) && ImageNode.isImageNode(nodes.children[0])) {
        return {
            ...nodes,
            children: nodes.children.slice(1),
        };
    }
    return nodes;
}

function Story({ story }: Props) {
    const {
        sharingActions,
        sharingPlacement,
        showDate,
        showCopyContent,
        showCopyUrl,
        showDownloadAssets,
    } = useThemeSettings();
    const { isEnabled: isAnalyticsEnabled } = useAnalyticsContext();

    const [onlyContactNodes, allNodesWithoutContacts] = useMemo(() => {
        const allNodes: DocumentNode = JSON.parse(story.content as string);

        return [extractContactNodes(allNodes), getFilteredNodes(removeContactNodes(allNodes))];
    }, [story.content]);

    if (!story) {
        return null;
    }

    const {
        title,
        subtitle,
        categories,
        links,
        slug,
        summary,
        thumbnail_url: thumbnailUrl,
        uuid,
        visibility,
    } = story;
    const headerImage = story.header_image ? JSON.parse(story.header_image) : null;
    const hasHeaderImage = Boolean(headerImage);
    const hasCategories = categories.length > 0;
    const sharingUrl = links.short || links.newsroom_view;

    const previewImage = getStoryThumbnail(story);
    const showSidebar = onlyContactNodes.children.length > 0 || previewImage;

    return (
        <Layout>
            <StorySeo story={story} noindex={!isAnalyticsEnabled} />
            <article
                className={classNames(styles.story, {
                    [styles.withSidebar]: showSidebar,
                })}
            >
                {headerImage && (
                    <Image
                        alt=""
                        className={styles.mainImage}
                        objectFit="cover"
                        layout="fill"
                        imageDetails={headerImage}
                    />
                )}
                <div
                    className={classNames(styles.container, {
                        [styles.withImage]: hasHeaderImage,
                        [styles.withSidebar]: showSidebar,
                    })}
                >
                    {isEmbargoStory(story) && <Embargo story={story} />}
                    <div className={styles.meta}>
                        {showDate && <StoryPublicationDate story={story} />}
                        {showDate && hasCategories && <> &middot; </>}
                        {hasCategories && (
                            <CategoriesList categories={categories} showAllCategories />
                        )}
                    </div>
                    <h1 className={styles.title}>{title}</h1>
                    <p className={styles.subtitle}>{subtitle}</p>
                    <ContentRenderer nodes={allNodesWithoutContacts} story={story} />
                </div>

                {showSidebar && (
                    <div className={styles.sidebar}>
                        {previewImage && (
                            <div className={styles.imageWrapper}>
                                <Image
                                    imageDetails={previewImage}
                                    alt={story.title}
                                    layout="fill"
                                    objectFit="cover"
                                    containerClassName={classNames(styles.imageContainer)}
                                    className={styles.image}
                                />
                                <div className={styles.middle}>
                                    <div className={styles.download}>
                                        <ButtonLink
                                            variation="primary"
                                            href={`https://cdn.uc.assets.prezly.com/${previewImage.uuid}/-/inline/no/`}
                                            className={styles.button}
                                        >
                                            <FormattedMessage {...translations.actions.download} />
                                        </ButtonLink>
                                    </div>
                                </div>
                            </div>
                        )}
                        {onlyContactNodes.children.length > 0 && (
                            <ContentRenderer nodes={onlyContactNodes} />
                        )}
                    </div>
                )}
            </article>
            {visibility === 'public' && (
                <Share
                    actions={{
                        showCopyContent,
                        showCopyUrl,
                        showDownloadAssets,
                    }}
                    thumbnailUrl={thumbnailUrl}
                    socialNetworks={sharingPlacement.includes('bottom') ? sharingActions : []}
                    summary={summary}
                    slug={slug}
                    title={title}
                    uploadcareAssetsGroupUuid={(story as any).uploadcare_assets_group_uuid}
                    url={sharingUrl}
                    uuid={uuid}
                />
            )}
        </Layout>
    );
}

export default Story;
